import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CREATE_FISCAL_PROFILE_MUTATION,
  SAT_CATALOG_ENTRIES_QUERY,
  SAT_POSTAL_CODE_QUERY,
  UPDATE_FISCAL_PROFILE_MUTATION,
} from './invoicing.gql';

import type { FiscalProfile } from './types';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Modal, ModalTitle } from '@/shared/ui/modal';

export interface SatEntry {
  code: string;
  description: string;
  extra_json: string | null;
}

export interface SatEntriesResponse {
  satCatalogEntries: SatEntry[];
}

interface SatPostalResponse {
  satPostalCode: { code: string; state_code: string; municipality_code: string }[];
}

/** SAT catalogs flag person-type applicability as "Sí"/"No" inside `extra_json`. */
export function appliesTo(entry: SatEntry, key: string): boolean {
  if (!entry.extra_json) return true;
  try {
    const extra = JSON.parse(entry.extra_json) as Record<string, string | undefined>;
    return extra[key] === undefined || extra[key] === 'Sí';
  } catch {
    return true;
  }
}

interface FiscalProfileFormDialogProps {
  customer_id: string;
  profile: FiscalProfile | null;
  on_close: () => void;
  on_done: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

export function FiscalProfileFormDialog({
  customer_id,
  profile,
  on_close,
  on_done,
  on_error: on_parent_error,
}: FiscalProfileFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  // Shown inside the modal: the parent's banner renders behind the overlay.
  const [error_text, setErrorText] = useState<string | null>(null);
  const on_error = (msg: string | null): void => {
    setErrorText(msg);
    on_parent_error(msg);
  };
  const is_edit = profile !== null;

  const [label, setLabel] = useState(profile?.label ?? '');
  const [rfc, setRfc] = useState(profile?.rfc ?? '');
  const [legal_name, setLegalName] = useState(profile?.legal_name ?? '');
  const [tax_regime_code, setTaxRegimeCode] = useState(profile?.tax_regime_code ?? '601');
  const [postal_code, setPostalCode] = useState(profile?.postal_code ?? '');
  const [street, setStreet] = useState(profile?.street ?? '');
  const [exterior_number, setExteriorNumber] = useState(profile?.exterior_number ?? '');
  const [interior_number, setInteriorNumber] = useState(profile?.interior_number ?? '');
  const [neighborhood, setNeighborhood] = useState(profile?.neighborhood ?? '');
  const [municipality, setMunicipality] = useState(profile?.municipality ?? '');
  const [state, setState] = useState(profile?.state ?? '');
  const [cfdi_usage, setCfdiUsage] = useState(profile?.cfdi_usage ?? 'G03');
  const [email, setEmail] = useState(profile?.email ?? '');
  const [is_default, setIsDefault] = useState<boolean>(profile?.is_default ?? false);

  const [createProfile, { loading: c_loading }] = useMutation(CREATE_FISCAL_PROFILE_MUTATION);
  const [updateProfile, { loading: u_loading }] = useMutation(UPDATE_FISCAL_PROFILE_MUTATION);
  const loading = c_loading || u_loading;

  // 12-char RFC = persona moral, 13 = física; filters régimen / uso CFDI to what applies.
  const rfc_len = rfc.trim().length;
  const is_moral = rfc_len === 12;
  const person_known = rfc_len === 12 || rfc_len === 13;

  const { data: regimes_data } = useQuery<SatEntriesResponse>(SAT_CATALOG_ENTRIES_QUERY, {
    variables: { catalog: 'c_RegimenFiscal', limit: 100 },
  });
  const { data: usages_data } = useQuery<SatEntriesResponse>(SAT_CATALOG_ENTRIES_QUERY, {
    variables: { catalog: 'c_UsoCFDI', limit: 100 },
  });
  const { data: postal_data } = useQuery<SatPostalResponse>(SAT_POSTAL_CODE_QUERY, {
    variables: { code: postal_code.trim() },
    skip: !/^\d{5}$/.test(postal_code.trim()),
  });

  const regime_options = (regimes_data?.satCatalogEntries ?? []).filter(
    (e) => !person_known || appliesTo(e, is_moral ? 'moral' : 'fisica') || e.code === tax_regime_code,
  );
  const usage_options = (usages_data?.satCatalogEntries ?? []).filter(
    (e) =>
      !person_known ||
      appliesTo(e, is_moral ? 'aplicaParaTipoPersonaMoral' : 'aplicaParaTipoPersonaFisica') ||
      e.code === cfdi_usage,
  );
  const catalogs_loaded = regime_options.length > 0 && usage_options.length > 0;
  const postal_checked = /^\d{5}$/.test(postal_code.trim()) && postal_data !== undefined;
  const postal_found = (postal_data?.satPostalCode.length ?? 0) > 0;

  const can_submit =
    !loading &&
    rfc.trim().length >= 12 &&
    legal_name.trim().length > 0 &&
    tax_regime_code.trim().length > 0 &&
    /^\d{5}$/.test(postal_code.trim()) &&
    cfdi_usage.trim().length > 0;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    try {
      if (is_edit && profile) {
        await updateProfile({
          variables: {
            input: {
              id: profile.id,
              label: label.trim() || null,
              rfc: rfc.trim().toUpperCase(),
              legal_name: legal_name.trim(),
              tax_regime_code: tax_regime_code.trim(),
              postal_code: postal_code.trim(),
              street: street.trim() || null,
              exterior_number: exterior_number.trim() || null,
              interior_number: interior_number.trim() || null,
              neighborhood: neighborhood.trim() || null,
              municipality: municipality.trim() || null,
              state: state.trim() || null,
              cfdi_usage: cfdi_usage.trim().toUpperCase(),
              email: email.trim().toLowerCase() || null,
              is_default,
            },
          },
        });
      } else {
        await createProfile({
          variables: {
            input: {
              customer_id,
              label: label.trim() || null,
              rfc: rfc.trim().toUpperCase(),
              legal_name: legal_name.trim(),
              tax_regime_code: tax_regime_code.trim(),
              postal_code: postal_code.trim(),
              street: street.trim() || null,
              exterior_number: exterior_number.trim() || null,
              interior_number: interior_number.trim() || null,
              neighborhood: neighborhood.trim() || null,
              municipality: municipality.trim() || null,
              state: state.trim() || null,
              cfdi_usage: cfdi_usage.trim().toUpperCase(),
              email: email.trim().toLowerCase() || null,
              is_default,
            },
          },
        });
      }
      await on_done();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'rfc_invalid') return on_error(t('invoicing.error_rfc_invalid'));
        if (code === 'postal_code_invalid')
          return on_error(t('invoicing.error_postal_code_invalid'));
        const sat_error_keys: Record<string, string> = {
          tax_regime_invalid: 'invoicing.error_tax_regime_invalid',
          tax_regime_person_type_mismatch: 'invoicing.error_tax_regime_person_type_mismatch',
          cfdi_usage_invalid: 'invoicing.error_cfdi_usage_invalid',
          cfdi_usage_person_type_mismatch: 'invoicing.error_cfdi_usage_person_type_mismatch',
          postal_code_not_in_sat_catalog: 'invoicing.error_postal_code_not_in_sat_catalog',
        };
        const sat_key = sat_error_keys[code];
        return on_error(sat_key ? t(sat_key) : code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <Modal on_close={on_close} dismissible={!loading} max_width="max-w-lg">
      <Card>
        <CardHeader>
          <ModalTitle>
            {is_edit ? t('invoicing.fiscal_profile_edit') : t('invoicing.fiscal_profile_create')}
          </ModalTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {error_text ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive md:col-span-2"
            >
              {error_text}
            </div>
          ) : null}
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_label')}</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={loading}
              placeholder={t('invoicing.field_label_placeholder')}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_rfc')} *</Label>
            <Input
              value={rfc}
              onChange={(e) => setRfc(e.target.value.toUpperCase())}
              disabled={loading}
              maxLength={13}
              className="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_postal_code')} *</Label>
            <Input
              value={postal_code}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              disabled={loading}
              inputMode="numeric"
              className="font-mono"
            />
            {postal_checked && catalogs_loaded ? (
              <p className={postal_found ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
                {postal_found
                  ? t('invoicing.postal_code_found', {
                      state: postal_data?.satPostalCode[0]?.state_code ?? '',
                    })
                  : t('invoicing.error_postal_code_not_in_sat_catalog')}
              </p>
            ) : null}
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_legal_name')} *</Label>
            <Input
              value={legal_name}
              onChange={(e) => setLegalName(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_tax_regime')} *</Label>
            {catalogs_loaded ? (
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={tax_regime_code}
                onChange={(e) => setTaxRegimeCode(e.target.value)}
                disabled={loading}
              >
                {regime_options.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} - {o.description}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                value={tax_regime_code}
                onChange={(e) => setTaxRegimeCode(e.target.value.replace(/\D/g, ''))}
                disabled={loading}
                className="font-mono"
                maxLength={4}
              />
            )}
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_cfdi_usage')} *</Label>
            {catalogs_loaded ? (
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={cfdi_usage}
                onChange={(e) => setCfdiUsage(e.target.value)}
                disabled={loading}
              >
                {usage_options.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} - {o.description}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                value={cfdi_usage}
                onChange={(e) => setCfdiUsage(e.target.value.toUpperCase())}
                disabled={loading}
                className="font-mono"
                maxLength={6}
              />
            )}
          </div>
          <div className="md:col-span-2">
            <p className="mt-1 text-xs font-medium text-muted-foreground">
              {t('invoicing.field_address_section')}
            </p>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_street')}</Label>
            <Input value={street} onChange={(e) => setStreet(e.target.value)} disabled={loading} />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_exterior_number')}</Label>
            <Input
              value={exterior_number}
              onChange={(e) => setExteriorNumber(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_interior_number')}</Label>
            <Input
              value={interior_number}
              onChange={(e) => setInteriorNumber(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_neighborhood')}</Label>
            <Input
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_municipality')}</Label>
            <Input
              value={municipality}
              onChange={(e) => setMunicipality(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_state')}</Label>
            <Input value={state} onChange={(e) => setState(e.target.value)} disabled={loading} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_email')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={is_default}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={loading}
            />
            <span>{t('invoicing.field_set_default')}</span>
          </label>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  );
}
